import React, { useState, useCallback, useRef, useEffect } from 'react';
import moment from 'moment';
import Contents from '../../../contents/index';
import axios from 'axios';
import Loader from '../../../../components/loader.jsx';
import BlogTagsInput from './tags_input';
import compressImage from '../../../../lib/image_compressor.js';

const Edit = ({
  blog,
  myCircle,
  blogTags,
  pastTags,
  isApp,
  imageUrlMedium,
  pdfUrl,
  userAgent,
}) => {
  const [title, setTitle] = useState(blog.title);
  const [image, setImage] = useState(imageUrlMedium);
  const [publishDatetime, setPublishDatetime] = useState(
    moment(blog.publish_datetime).format('YYYY-MM-DD')
  );
  const [publicType, setPublicType] = useState(blog.public_type);
  const [isDraft, setIsDraft] = useState(blog.is_draft);
  const [isDeleted, setIsDeleted] = useState(false);
  const [tags, setTags] = useState(blogTags || []);
  const [eventId, setEventId] = useState(blog.event_id);
  const [uploading, setUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdf, setPdf] = useState(pdfUrl);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  // スマホの並べかえモード。行のメニューの「並べかえ」（components/contents）から切り替わる
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    const startReorder = () => setIsReordering(true);
    window.addEventListener('blog-editor:reorder', startReorder);
    return () => window.removeEventListener('blog-editor:reorder', startReorder);
  }, []);

  const inputRef = useRef(null);

  const updateImage = useCallback(
    async (photoFile) => {
      const compressedFile = await compressImage(photoFile);
      setUploading(true);
      let formData = new FormData();
      formData.append('file', compressedFile);
      axios
        .patch(`/api/my_circle/blog/${blog.id}/update_image`, formData)
        .then((response) => {
          setImage(response.data.image_url_medium);
        })
        .catch(() => {})
        .finally(() => {
          setUploading(false);
        });
    },
    [blog]
  );

  const updateBlogToServer = (status, boolean) => {
    $.ajax({
      url: '/api/my_circle/blog/update',
      type: 'POST',
      cache: false,
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
      data: {
        id: blog.id,
        event_id: eventId,
        title: title,
        content: '',
        publish_datetime: publishDatetime,
        public_type: publicType,
        is_draft: status === 'draft' ? boolean : isDraft,
        is_deleted: status === 'delete' ? boolean : isDeleted,
        tags: tags.join(' '),
      },
    })
      .done(() => {
        window.location.href = `/my_circle/${myCircle.id}/blogs/`;
      })
      .fail(() => {});
  };

  const handleTitleInput = (e) => {
    setTitle(e.target.value);
  };

  const handleTagClick = (tag) => {
    if (!tags.includes(tag)) {
      const updatedTags = [...tags, tag];
      setTags(updatedTags);
    }
  };

  const handleTagsInput = (updatedTags) => {
    setTags(updatedTags);
  };

  // 開いたときの記事の状態（見た目の出し分けだけに使う。押したときの動きは変えない）
  const isPublished = !blog.is_draft;
  const isScheduled =
    isPublished && moment(blog.publish_datetime).isAfter(moment());
  const statusLabel = !isPublished
    ? '下書き・非公開'
    : isScheduled
    ? `公開予約 ${moment(blog.publish_datetime).format('M/D')}`
    : '公開中';
  const statusClass = !isPublished
    ? 'is-draft'
    : isScheduled
    ? 'is-scheduled'
    : 'is-published';

  const handleDraftButton = () => {
    var result = window.confirm(
      isPublished
        ? '下書きに戻しますか？ほかの人から見えなくなります。'
        : '下書きで保存しますか？'
    );
    if (result) {
      updateBlogToServer('draft', true);
    }
  };

  const handleDeleteButton = () => {
    var result = window.confirm('このブログを削除しますか？');
    if (result) {
      updateBlogToServer('delete', true);
    }
  };

  const handleSubmitButton = () => {
    var result = window.confirm(isPublished ? '更新しますか？' : '公開しますか？');
    if (result) {
      updateBlogToServer('draft', false);
    }
  };

  const handlePostDateChange = (e) => {
    setPublishDatetime(e.target.value);
  };

  const handleKeydown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      e.target.blur();
    }
  };

  const updatePdf = useCallback(
    (e) => {
      setPdfUploading(true);

      if (e.target.files.length === 0) {
        setPdfUploading(false);
        return;
      }
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('file', file);
      axios
        .patch(`/api/my_circle/blog/${blog.id}/update_pdf`, formData)
        .then((response) => {
          setPdf(response.data.pdf_url);
        })
        .catch(() => {})
        .finally(() => {
          setPdfUploading(false);
          // ファイル入力をリセット
          e.target.value = null;
        });
    },
    [blog]
  );

  const deletePdf = useCallback(() => {
    if (!pdf) return;
    if (confirm('PDFを削除しますか？')) {
      axios
        .post(`/api/my_circle/blog/${blog.id}/delete_pdf`)
        .then(() => {
          setPdf(null);
        })
        .catch(() => {});
    }
  }, [pdf, blog]);

  return (
    <div
      className={`be-2026${userAgent === 'pc' ? ' is-pc' : ''}${
        isDetailOpen ? ' is-panel-open' : ''
      }${isReordering ? ' is-reordering' : ''}`}
    >
      <div
        className="be-bar"
        style={{
          position: 'sticky',
          // ヘッダーの高さ（PC 56px・スマホ 47px）のすぐ下に止める。画面の高さの割合だとすき間ができていた
          top: userAgent == 'pc' ? '56px' : isApp ? '0%' : '47px',
          zIndex: '20',
        }}
      >
        <div className="be-wrap be-bar__inner">
          <p className="be-bar__title">ブログ作成</p>
          <span className={`be-status ${statusClass}`}>{statusLabel}</span>
          <div className="be-bar__actions">
            <button
              type="button"
              className={`be-btn be-btn--outline${isDetailOpen ? ' is-active' : ''}`}
              aria-expanded={isDetailOpen}
              onClick={() => setIsDetailOpen((prev) => !prev)}
            >
              詳細設定
            </button>
            <button
              type="button"
              className="be-btn be-btn--outline"
              onClick={handleDraftButton}
            >
              {isPublished ? '下書きに戻す' : '下書き'}
            </button>
            <button
              type="button"
              onClick={handleSubmitButton}
              className="be-btn be-btn--primary"
            >
              {isPublished ? '更新' : '公開'}
            </button>
          </div>
        </div>
      </div>
      <div className="be-layout">
        <div className="be-main">
          <div className="be-wrap be-head">
            <figure className="be-cover">
              <label htmlFor="image_data" className="be-cover__label">
                {uploading ? (
                  <div className="be-cover__frame be-cover__frame--loading">
                    <Loader isActive={true} />
                  </div>
                ) : image ? (
                  <div className="be-cover__frame">
                    <img src={image} alt="" className="be-cover__img" />
                    <span className="be-cover__edit">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                      <span className="be-sr">画像を変更</span>
                    </span>
                  </div>
                ) : (
                  <div className="be-cover__empty">
                    <span className="be-cover__plus" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                    </span>
                    <span className="be-cover__lead">カバー画像を追加</span>
                    <span className="be-cover__note">
                      対応形式：JPG・PNG・HEIC
                    </span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  onChange={(e) => {
                    updateImage(e.target.files[0]);
                  }}
                  id="image_data"
                  style={{ display: 'none' }}
                />
              </label>
            </figure>
            <textarea
              type="text"
              className="textarea-no-border be-title"
              placeholder="ブログタイトル"
              value={title}
              onKeyDown={(e) => handleKeydown(e)}
              onChange={handleTitleInput}
            />
          </div>
          <div className="be-body">
            <input
              type="file"
              ref={inputRef}
              accept="application/pdf"
              onChange={updatePdf}
              className="d-none"
            />
            {pdfUploading ? (
              <div className="be-wrap be-pdf__loading">
                <Loader isActive={true} />
              </div>
            ) : pdf ? (
              <div className="be-wrap">
                {/* PDFで作っているときは、本文に戻る道を小さく出す */}
                <div className="be-pdf__actions">
                  <button
                    type="button"
                    className="be-btn be-btn--outline be-btn--pdf be-btn--small"
                    onClick={() => {
                      inputRef.current && inputRef.current.click();
                    }}
                  >
                    PDFを変更
                  </button>
                  <button
                    type="button"
                    className="be-btn be-btn--outline be-btn--small be-btn--danger"
                    onClick={deletePdf}
                  >
                    PDFを削除して本文で書く
                  </button>
                </div>
                {/* ブラウザのPDF表示の左のサムネイルの列を出さない（navpanes=0。Chrome などで効く） */}
                <iframe src={`${pdf}#navpanes=0`} className="be-pdf__frame" />
              </div>
            ) : (
              // 何も書いていないときは、左に本文の入力・右にPDFを同じ大きさで並べる。
              // 本文を書き始めたら（行が1つでもできたら）、PDFは小さな案内にする（見た目の切り替えは CSS）
              <div className="be-wrap be-split">
                <div className="be-split__write">
                  <Contents
                    foreignKeyId={blog.id}
                    isApp={isApp}
                    userAgent={userAgent}
                    model={'Blog'}
                  />
                </div>
                <p className="be-split__or">または</p>
                <button
                  type="button"
                  className="be-split__pdf"
                  onClick={() => {
                    inputRef.current && inputRef.current.click();
                  }}
                >
                  <span className="be-split__pdficon" aria-hidden="true" />
                  <span className="be-split__pdflead">PDFで記事をアップロード</span>
                  <span className="be-split__pdfnote">
                    本文の入力は使えなくなります（PDFを削除すると戻せます）
                  </span>
                  <span className="be-split__pdfsmall">
                    PDFで記事をアップロード
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
        {isReordering && (
          <div className="be-reorderbar">
            <p className="be-reorderbar__text">
              点6つをつかんで順番を入れかえます
            </p>
            <button
              type="button"
              className="be-btn be-reorderbar__done"
              onClick={() => setIsReordering(false)}
            >
              完了
            </button>
          </div>
        )}
        <div
          className="be-panel__overlay"
          onClick={() => setIsDetailOpen(false)}
        />
        <aside className="be-panel" aria-label="詳細設定">
          <DetailContent
            publicType={publicType}
            publishDatetime={publishDatetime}
            tags={tags}
            setPublicType={setPublicType}
            handlePostDateChange={handlePostDateChange}
            handleNowClick={() =>
              setPublishDatetime(moment().format('YYYY-MM-DD'))
            }
            handleTagClick={handleTagClick}
            handleTagsInput={handleTagsInput}
            handleDeleteButton={handleDeleteButton}
            myCircle={myCircle}
            pastTags={pastTags}
            onClose={() => setIsDetailOpen(false)}
          />
        </aside>
      </div>
    </div>
  );
};

export default Edit;

const DetailContent = ({
  publicType,
  publishDatetime,
  tags,
  setPublicType,
  handlePostDateChange,
  handleNowClick,
  handleTagClick,
  handleTagsInput,
  handleDeleteButton,
  myCircle,
  pastTags,
  onClose,
}) => {
  // 前に使ったカテゴリのうち、この記事にまだ付けていないものだけを候補に出す
  const unusedPastTags = (pastTags || []).filter((tag) => !tags.includes(tag));
  return (
    <div className="be-panel__inner">
      <div className="be-panel__head">
        <p className="be-panel__title">詳細設定</p>
        <button
          type="button"
          className="be-panel__close"
          aria-label="閉じる"
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
      {myCircle && (
        <section className="be-panel__section">
          <p className="be-panel__label">公開範囲</p>
          <div className="be-scope" role="radiogroup" aria-label="公開範囲">
            {[
              ['1', '全体公開', 'だれでも読めます', 'all'],
              ['2', 'サークル内限定', 'サークルのメンバーだけが読めます', 'members'],
            ].map(([value, label, note, icon]) => (
              <label key={value} className="be-scope__item">
                <input
                  type="radio"
                  name="be-public-type"
                  className="be-radio"
                  value={value}
                  checked={String(publicType) === value}
                  onChange={(e) => setPublicType(e.target.value)}
                />
                <span
                  className={`be-panel__scope be-panel__scope--${icon}`}
                  aria-hidden="true"
                />
                <span className="be-scope__text">
                  <span className="be-scope__label">{label}</span>
                  <span className="be-scope__note">{note}</span>
                </span>
              </label>
            ))}
          </div>
        </section>
      )}
      <section className="be-panel__section">
        <div className="be-panel__labelrow">
          <p className="be-panel__label">公開日</p>
          <button
            type="button"
            className="be-panel__now"
            onClick={handleNowClick}
          >
            今日にする
          </button>
        </div>
        <input
          type="date"
          className="be-panel__input"
          value={publishDatetime}
          onChange={handlePostDateChange}
        />
        <p className="be-panel__info">
          未来の日付にすると、その日の0時に自動で公開されます。
        </p>
      </section>
      <section className="be-panel__section">
        <p className="be-panel__label">カテゴリ</p>
        <div className="be-panel__tags">
          <BlogTagsInput blogTags={tags} handleTagsInput={handleTagsInput} />
        </div>
        <p className="be-panel__note">
          入力してEnter（改行）で追加できます。スペースは使えません（入れると保存のときに分かれます）。
        </p>
        {unusedPastTags.length > 0 && (
          <div className="be-panel__past">
            <p className="be-panel__sublabel">前に使ったカテゴリ</p>
            <ul className="be-panel__chips">
              {unusedPastTags.map((tag, index) => (
                <li
                  key={index}
                  onClick={() => handleTagClick(tag)} // タグをクリックした際にタグを追加
                  className="be-panel__chip"
                >
                  {tag}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
      <section className="be-panel__section be-panel__section--last">
        <button
          type="button"
          className="be-panel__delete"
          onClick={handleDeleteButton}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
          このブログを削除する
        </button>
      </section>
    </div>
  );
};
